import React, { useEffect, useRef, useState } from "react";
import { Tag, ChevronDown, X } from "lucide-react";
import api from "../../lib/api";

// Dropdown multi-select de tags, usado ao lado da busca de leads no topo do
// kanban (Dashboard.jsx). Controlado de fora (`selected`/`onChange`) pra que
// o componente que faz o fetch dos leads (Dashboard) decida quando refiltrar.
// Lista de tags disponíveis vem de GET /leads/tags (tags distintas do tenant).
export default function TagFilterDropdown({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const boxRef = useRef(null);

  const safeSelected = Array.isArray(selected) ? selected : [];

  useEffect(() => {
    setLoading(true);
    api.get("/leads/tags")
      .then(({ data }) => setOptions(Array.isArray(data?.tags) ? data.tags : []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const toggleTag = (tag) => {
    const next = safeSelected.includes(tag)
      ? safeSelected.filter((t) => t !== tag)
      : [...safeSelected, tag];
    onChange?.(next);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange?.([]);
  };

  if (!loading && options.length === 0) return null;

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        data-testid="tag-filter-trigger"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 pl-3 pr-2 py-2 rounded-lg text-sm border transition ${
          safeSelected.length > 0
            ? "border-[#25D366]/60 bg-[#25D366]/10 text-[#25D366]"
            : "border-slate-700 bg-slate-800/60 text-slate-300 hover:bg-slate-800"
        }`}
      >
        <Tag className="w-4 h-4" />
        Tags
        {safeSelected.length > 0 && (
          <span
            data-testid="tag-filter-count"
            className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#25D366] text-slate-950 text-[10px] font-bold"
          >
            {safeSelected.length}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="tag-filter-panel"
          className="absolute right-0 mt-2 w-64 max-h-80 overflow-y-auto bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-50"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Filtrar por tags
            </span>
            {safeSelected.length > 0 && (
              <button
                type="button"
                data-testid="tag-filter-clear"
                onClick={clearAll}
                className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-red-400 transition"
              >
                <X className="w-3 h-3" /> Limpar
              </button>
            )}
          </div>
          {options.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-slate-500">
              {loading ? "Carregando..." : "Nenhuma tag cadastrada"}
            </div>
          ) : (
            <div className="py-1">
              {options.map((tag) => {
                const checked = safeSelected.includes(tag);
                return (
                  <label
                    key={tag}
                    data-testid={`tag-filter-option-${tag}`}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 cursor-pointer transition"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleTag(tag)}
                      className="accent-[#25D366] w-3.5 h-3.5"
                    />
                    {tag}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
